import React, { useState, useEffect, useMemo } from 'react';
import { BarChart2, ArrowUpRight, ArrowDownRight, Minus, Clock, RefreshCw, AlertCircle, CheckCircle2, Search, FlaskConical, Download, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { auth } from '../../firebase';
import { BacktestEntry, VerdictBucketStats, BacktestResponse, HistoricalDCFDataPoint, HistoricalDCFResponse } from '../../types/backtestTypes';
import {
  saveHistoricalDcfScan,
  subscribeToHistoricalDcfScans,
  deleteHistoricalDcfScan,
  clearAllHistoricalDcfScans,
} from '../../services/historicalDcfService';

function formatPct(val: number | null, pending: boolean): string | null {
  if (pending) return null; // show Clock icon
  if (val === null) return '—';
  const sign = val >= 0 ? '+' : '';
  return `${sign}${(val * 100).toFixed(1)}%`;
}

function pctColor(val: number | null, pending: boolean): string {
  if (val === null || pending) return 'text-ink-400';
  return val >= 0 ? 'text-[#2F6B57] font-semibold' : 'text-[#B14228] font-semibold';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

export function BacktestTab({ isProduction = false }: { isProduction?: boolean }) {
  const [data, setData] = useState<BacktestResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<'alpha90d' | 'return90d' | 'ticker' | 'date'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [filterBucket, setFilterBucket] = useState<'all' | 'buy' | 'hold' | 'pass'>('all');
  // [TRACK-1] Superseded dups + regression runs are hidden by default.
  const [showSupersededRegression, setShowSupersededRegression] = useState<boolean>(false);

  // ── Sub-tab ──────────────────────────────────────────────────────────────
  const [activeSubTab, setActiveSubTab] = useState<'tracker' | 'historical-dcf'>('tracker');

  // ── Historical DCF state ─────────────────────────────────────────────────
  const [hdcfTicker, setHdcfTicker] = useState('');
  const [hdcfData, setHdcfData] = useState<HistoricalDCFResponse | null>(null);
  const [hdcfLoading, setHdcfLoading] = useState(false);
  const [hdcfError, setHdcfError] = useState<string | null>(null);

  // ── Scan history — Firestore in prod, localStorage in dev ────────────────
  // AI Studio sandbox cannot write to the named Firestore database via the
  // client SDK (same class of issue as server-side admin SDK dev bypasses).
  const LS_KEY = 'oracle_dcf_scan_history';

  const [scanHistory, setScanHistory] = useState<Map<string, HistoricalDCFResponse>>(() => {
    if (!isProduction) {
      try {
        const saved = localStorage.getItem(LS_KEY);
        if (saved) return new Map(JSON.parse(saved) as [string, HistoricalDCFResponse][]);
      } catch {}
    }
    return new Map();
  });

  useEffect(() => {
    if (!isProduction) return; // dev: localStorage only, no Firestore subscription

    let unsubFirestore: (() => void) | undefined;
    const unsubAuth = auth.onAuthStateChanged((user) => {
      if (unsubFirestore) { unsubFirestore(); unsubFirestore = undefined; }
      if (!user?.uid) return;
      unsubFirestore = subscribeToHistoricalDcfScans(user.uid, (scans) => {
        const map = new Map<string, HistoricalDCFResponse>();
        for (const scan of scans) map.set(scan.ticker, scan);
        setScanHistory(map);
      });
    });
    return () => { unsubAuth(); if (unsubFirestore) unsubFirestore(); };
  }, [isProduction]);

  function addToScanHistory(result: HistoricalDCFResponse) {
    setScanHistory(prev => {
      const next = new Map(prev).set(result.ticker, result);
      if (!isProduction) {
        try { localStorage.setItem(LS_KEY, JSON.stringify([...next.entries()])); } catch {}
      }
      return next;
    });
    if (isProduction) {
      const uid = auth.currentUser?.uid;
      if (uid) saveHistoricalDcfScan(uid, result);
    }
  }

  function clearScanHistory() {
    setScanHistory(new Map());
    if (!isProduction) {
      try { localStorage.removeItem(LS_KEY); } catch {}
    } else {
      const uid = auth.currentUser?.uid;
      if (uid) clearAllHistoricalDcfScans(uid);
    }
  }

  function exportScanHistoryCSV() {
    const rows: string[] = [
      'Ticker,Company,FY,FCF_M,Growth3yr_pct,WACC_pct,TerminalMultiple,IV,ActualPrice,MoS_pct,Return1yr_pct,Return1yr_Pending'
    ];
    for (const scan of scanHistory.values()) {
      for (const dp of scan.dataPoints) {
        rows.push([
          scan.ticker,
          `"${scan.companyName.replace(/"/g, '""')}"`,
          dp.fiscalYear,
          dp.fcfMillions,
          dp.growthRate3yr !== null ? (dp.growthRate3yr * 100).toFixed(2) : '',
          dp.assumptionsUsed?.wacc !== undefined ? (dp.assumptionsUsed.wacc * 100).toFixed(1) : '9.0',
          dp.assumptionsUsed?.terminalMultiple ?? 15,
          dp.intrinsicValuePerShare !== null ? dp.intrinsicValuePerShare.toFixed(2) : '',
          dp.actualPrice !== null ? dp.actualPrice.toFixed(2) : '',
          dp.marginOfSafety !== null ? (dp.marginOfSafety * 100).toFixed(2) : '',
          dp.forwardReturn1yr !== null ? (dp.forwardReturn1yr * 100).toFixed(2) : '',
          dp.forwardReturn1yrPending ? 'TRUE' : 'FALSE',
        ].join(','));
      }
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oracle_dcf_backtest_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function exportVerdictTrackerCSV() {
    if (!data?.entries?.length) return;

    const rows: string[] = [
      'Ticker,Date,Verdict,VerdictBucket,IV,PriceAtAnalysis,MoS_pct,Return30d_pct,Return90d_pct,Return365d_pct,SPY30d_pct,SPY90d_pct,SPY365d_pct,Alpha30d_pct,Alpha90d_pct,Alpha365d_pct,Pending30d,Pending90d,Pending365d,TickerNormalized,BuildId,GitSha,Env,Superseded,IsRegressionRun,Degenerate,PriceAtVerdict,PriceEodBase,MoS_Recomputed_pct,MoS_Delta_pp,ModelOracle'
    ];

    for (const entry of data.entries) {
      const fmt = (n: number | null) => n !== null ? (n * 100).toFixed(2) : '';
      const fmtBool = (b: boolean) => b ? 'TRUE' : 'FALSE';
      
      rows.push([
        entry.ticker,
        entry.analyzedAt ? entry.analyzedAt.split('T')[0] : '',
        entry.verdict,
        entry.verdictBucket,
        entry.intrinsicValue ? entry.intrinsicValue.toFixed(2) : '',
        entry.priceAtAnalysis !== null ? entry.priceAtAnalysis.toFixed(2) : '',
        entry.marginOfSafety !== null && entry.marginOfSafety !== undefined ? Number(entry.marginOfSafety).toFixed(2) : '',
        fmt(entry.return30d),
        fmt(entry.return90d),
        fmt(entry.return365d),
        fmt(entry.spyReturn30d),
        fmt(entry.spyReturn90d),
        fmt(entry.spyReturn365d),
        fmt(entry.alpha30d),
        fmt(entry.alpha90d),
        fmt(entry.alpha365d),
        fmtBool(entry.pending30d),
        fmtBool(entry.pending90d),
        fmtBool(entry.pending365d),
        // [TRACK-1] ingestion-hardening + provenance columns.
        entry.tickerNormalized ?? '',
        entry.buildId ?? '',
        entry.gitSha ?? '',
        entry.env ?? '',
        fmtBool(!!entry.superseded),
        fmtBool(!!entry.isRegressionRun),
        entry.degenerate ?? '',
        entry.priceAtVerdict != null ? entry.priceAtVerdict.toFixed(2) : '',
        entry.priceEodBase != null ? entry.priceEodBase.toFixed(2) : '',
        entry.mosRecomputed != null ? entry.mosRecomputed.toFixed(2) : '',
        entry.mosReconcileDeltaPp != null ? entry.mosReconcileDeltaPp.toFixed(2) : '',
        entry.modelOracle ?? '',
      ].join(','));
    }

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oracle_verdict_tracker_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const [selectedHistoryTicker, setSelectedHistoryTicker] = useState<string | null>(null);

  function loadFromHistory(ticker: string) {
    const cached = scanHistory.get(ticker);
    if (!cached) return;
    setHdcfTicker(ticker);
    setHdcfData(cached);
    setHdcfError(null);
    setSelectedHistoryTicker(ticker);
  }

  const fetchHistoricalDCF = async (tickerInput: string) => {
    const t = tickerInput.trim().toUpperCase();
    if (!t) return;
    setHdcfLoading(true);
    setHdcfError(null);
    if (t !== hdcfData?.ticker) {
      setHdcfData(null);
    }
    try {
      const token = await new Promise<string>((resolve, reject) => {
        const unsub = auth.onAuthStateChanged((user) => {
          unsub();
          if (user) {
            user.getIdToken().then(resolve).catch(reject);
          } else {
            reject(new Error('Not authenticated'));
          }
        });
      });
      const res = await fetch(`/api/backtest/historical-dcf?ticker=${encodeURIComponent(t)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to fetch historical DCF');
      setHdcfData(json);
      if (json.message === 'ok') addToScanHistory(json);
      setSelectedHistoryTicker(json.ticker ?? tickerInput.trim().toUpperCase());
    } catch (e: any) {
      setHdcfError(e.message || 'Unknown error');
    } finally {
      setHdcfLoading(false);
    }
  };

  const fetchPerformance = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await new Promise<string>((resolve, reject) => {
        const unsub = auth.onAuthStateChanged((user) => {
          unsub();
          if (user) {
            user.getIdToken().then(resolve).catch(reject);
          } else {
            reject(new Error('Not authenticated'));
          }
        });
      });

      const res = await fetch('/api/backtest/performance', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to fetch backtest performance');
      
      setData(json);
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformance();
  }, []);

  const filteredEntries = useMemo(() => {
    if (!data?.entries) return [];
    let list = data.entries;
    // [TRACK-1] Hide superseded dups and regression runs unless the toggle is on.
    if (!showSupersededRegression) {
      list = list.filter(e => !e.superseded && !e.isRegressionRun);
    }
    if (filterBucket !== 'all') {
      list = list.filter(e => e.verdictBucket === filterBucket);
    }
    return list.sort((a, b) => {
      let valA: any, valB: any;
      switch (sortKey) {
        case 'alpha90d': valA = a.alpha90d ?? -9999; valB = b.alpha90d ?? -9999; break;
        case 'return90d': valA = a.return90d ?? -9999; valB = b.return90d ?? -9999; break;
        case 'ticker': valA = a.ticker.toLowerCase(); valB = b.ticker.toLowerCase(); break;
        case 'date': valA = new Date(a.analyzedAt).getTime(); valB = new Date(b.analyzedAt).getTime(); break;
        default: valA = 0; valB = 0;
      }
      
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, filterBucket, sortKey, sortDir, showSupersededRegression]);

  const toggleSort = (key: 'alpha90d' | 'return90d' | 'ticker' | 'date') => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc'); // Default new sort to desc
    }
  };

  const getVerdictPillStyle = (verdictBucket: string) => {
    if (verdictBucket === 'buy') return 'bg-[#2F6B57] text-white';
    if (verdictBucket === 'hold') return 'bg-[#8A5A1A] text-white';
    if (verdictBucket === 'pass') return 'bg-ink-600 text-white';
    return 'bg-ink-300 text-ink-900';
  };

  const getCardStyle = (bucket: string) => {
    if (bucket === 'buy') return 'border-[#2F6B57]/30';
    if (bucket === 'hold') return 'border-[#8A5A1A]/30';
    if (bucket === 'pass') return 'border-ink-200';
    return 'border-ink-200';
  };


  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">

      {/* ── Sub-tab switcher ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 border-b border-ink-200">
        <button
          onClick={() => setActiveSubTab('tracker')}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest border-b-2 -mb-px transition-colors',
            activeSubTab === 'tracker'
              ? 'border-oxblood text-oxblood'
              : 'border-transparent text-ink-500 hover:text-ink'
          )}
        >
          <BarChart2 className="w-3.5 h-3.5" />
          Verdict Tracker
        </button>
        <button
          onClick={() => setActiveSubTab('historical-dcf')}
          className={cn(
            'flex items-center gap-2 px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest border-b-2 -mb-px transition-colors',
            activeSubTab === 'historical-dcf'
              ? 'border-oxblood text-oxblood'
              : 'border-transparent text-ink-500 hover:text-ink'
          )}
        >
          <FlaskConical className="w-3.5 h-3.5" />
          Historical DCF
        </button>
      </div>

      {/* ── Historical DCF Sub-Tab ─────────────────────────────────────────── */}
      {activeSubTab === 'historical-dcf' && (
        <div className="space-y-6">

          {/* Header */}
          <div className="oi-card p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <h2 className="font-serif text-lg font-semibold text-ink">Historical DCF Scan</h2>
                <p className="text-[12px] text-ink-500 mt-0.5">
                  Tests the DCF framework against actual SEC EDGAR filings. Deterministic only — no AI judgment applied.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={hdcfTicker}
                  onChange={e => setHdcfTicker(e.target.value.toUpperCase())}
                  onKeyDown={e => { if (e.key === 'Enter') fetchHistoricalDCF(hdcfTicker); }}
                  placeholder="TICKER"
                  className="w-28 px-3 py-2 text-sm font-mono border border-ink-200 rounded-md bg-surface text-ink placeholder:text-ink-400 focus:outline-none focus:ring-1 focus:ring-oxblood uppercase"
                  maxLength={10}
                />
                <button
                  onClick={() => fetchHistoricalDCF(hdcfTicker)}
                  disabled={hdcfLoading || !hdcfTicker.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-bold uppercase tracking-widest bg-oxblood text-paper rounded-md disabled:opacity-40 hover:bg-oxblood/90 transition-colors"
                >
                  {hdcfLoading
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    : <Search className="w-3.5 h-3.5" />
                  }
                  {hdcfLoading ? 'Loading…' : 'Analyze'}
                </button>
              </div>
            </div>
          </div>

          {/* Persistent scan history panel */}
          {scanHistory.size > 0 && (
            <div className="oi-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-ink-400">
                  Scan History · {scanHistory.size} {scanHistory.size === 1 ? 'company' : 'companies'} · {[...scanHistory.values()].reduce((n, s) => n + s.dataPoints.filter(d => !d.skipped).length, 0)} data points
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportScanHistoryCSV}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest border border-ink-200 rounded-md text-ink-600 hover:bg-surface-2 transition-colors"
                  >
                    <Download className="w-3 h-3" />
                    Export CSV
                  </button>
                  <button
                    onClick={() => { clearScanHistory(); setSelectedHistoryTicker(null); }}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest border border-ink-200 rounded-md text-ink-500 hover:text-[#B14228] hover:border-[#B14228]/30 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    Clear
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {[...scanHistory.values()].sort((a, b) => a.ticker.localeCompare(b.ticker)).map(scan => (
                  <div
                    key={scan.ticker}
                    className={cn(
                      'flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-md border transition-colors cursor-pointer',
                      selectedHistoryTicker === scan.ticker
                        ? 'border-oxblood bg-oxblood/5 text-oxblood'
                        : 'border-ink-200 hover:border-ink-300 hover:bg-surface-2'
                    )}
                    onClick={() => loadFromHistory(scan.ticker)}
                  >
                    <span className="font-mono font-bold text-[12px]">{scan.ticker}</span>
                    <span className={cn('text-[10px]', selectedHistoryTicker === scan.ticker ? 'text-oxblood/70' : 'text-ink-400')}>
                      {scan.yearsAnalyzed}yr
                    </span>
                    <button
                      onClick={e => { e.stopPropagation(); fetchHistoricalDCF(scan.ticker); }}
                      title="Refresh from EDGAR"
                      className={cn(
                        'p-1 rounded transition-colors',
                        hdcfLoading && hdcfTicker === scan.ticker
                          ? 'text-oxblood'
                          : 'text-ink-400 hover:text-ink'
                      )}
                    >
                      <RefreshCw className={cn('w-3 h-3', hdcfLoading && hdcfTicker === scan.ticker && 'animate-spin')} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading — only show full spinner when no existing data to display */}
          {hdcfLoading && !hdcfData && (
            <div className="flex items-center justify-center gap-3 py-16 text-ink-500">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span className="text-sm">Fetching EDGAR filings and historical prices…</span>
            </div>
          )}

          {/* Error */}
          {hdcfError && !hdcfLoading && (
            <div className="oi-card p-5 border-danger/30 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#B14228] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-ink">{hdcfError}</p>
                <button onClick={() => fetchHistoricalDCF(hdcfTicker)} className="text-[11px] text-oxblood mt-1 hover:underline">Retry</button>
              </div>
            </div>
          )}

          {/* No EDGAR data */}
          {hdcfData?.message === 'no_edgar_data' && !hdcfLoading && (
            <div className="oi-card p-8 text-center text-ink-500 text-sm">
              <p>No EDGAR data found for <span className="font-mono font-bold text-ink">{hdcfData.ticker}</span>.</p>
              <p className="mt-1 text-[11px]">This ticker may not be a US-listed company or may not file with the SEC.</p>
            </div>
          )}

          {/* Insufficient data */}
          {hdcfData?.message === 'insufficient_data' && !hdcfLoading && hdcfData.dataPoints.length === 0 && (
            <div className="oi-card p-5 border-warning/30">
              <p className="text-sm text-ink">No usable FCF data found for <span className="font-mono font-bold">{hdcfData.ticker}</span>. This may be a financial entity (bank, insurer, conglomerate) where traditional FCF-based DCF does not apply.</p>
              {hdcfData.warnings.length > 0 && (
                <ul className="mt-2 text-[11px] text-ink-500 space-y-0.5">
                  {hdcfData.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Results table */}
          {hdcfData && (hdcfData.message === 'ok' || (hdcfData.message === 'insufficient_data' && hdcfData.dataPoints.length > 0)) && (
            <div className={cn('space-y-4', hdcfLoading && 'opacity-60 pointer-events-none')}>
              {hdcfData.message === 'insufficient_data' && (
                <div className="oi-card p-4 border-warning/30 text-[12px] text-ink-600">
                  ⚠ Partial data — fewer than 3 years of positive FCF found. This company may be a capital-intensive conglomerate or financial entity where standard FCF-based DCF has limited applicability. Skipped rows indicate years with negative FCF.
                </div>
              )}
              {/* Company header */}
              <div className="flex items-baseline gap-3">
                <h3 className="font-serif text-xl font-semibold text-ink">{hdcfData.companyName}</h3>
                <span className="font-mono text-sm text-ink-500">{hdcfData.ticker}</span>
                <span className="text-[11px] text-ink-400 ml-auto">{hdcfData.yearsAnalyzed} years analyzed</span>
              </div>

              {/* Warnings */}
              {hdcfData.warnings.length > 0 && (
                <div className="text-[11px] text-ink-500 space-y-0.5">
                  {hdcfData.warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}
                </div>
              )}

              {/* Table */}
              <div className="oi-card overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-ink-200">
                      {['FY', 'FCF ($M)', '3yr Growth', 'Est. IV', 'Actual Price', 'MoS', '1yr Fwd Return'].map(h => (
                        <th key={h} className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {hdcfData.dataPoints.map(dp => {
                      const isUndervalued = dp.marginOfSafety !== null && dp.marginOfSafety > 0.05;
                      const isOvervalued  = dp.marginOfSafety !== null && dp.marginOfSafety < -0.05;
                      return (
                        <tr
                          key={dp.fiscalYear}
                          className={cn(
                            'border-b border-ink-100 transition-colors',
                            dp.skipped ? 'opacity-50' : '',
                            isUndervalued ? 'bg-[#2F6B57]/5 hover:bg-[#2F6B57]/10' : '',
                            isOvervalued  ? 'bg-[#B14228]/5 hover:bg-[#B14228]/10' : 'hover:bg-surface-2/50'
                          )}
                        >
                          <td className="px-4 py-3 font-mono font-bold text-ink">{dp.fiscalYear}</td>
                          <td className="px-4 py-3 font-mono text-ink-600">
                            {dp.skipped ? <span className="text-ink-400 text-[11px]">{dp.skipReason}</span> : `$${dp.fcfMillions.toLocaleString()}`}
                          </td>
                          <td className={cn('px-4 py-3 font-mono', dp.growthRate3yr === null ? 'text-ink-400' : dp.growthRate3yr >= 0 ? 'text-[#2F6B57]' : 'text-[#B14228]')}>
                            {dp.growthRate3yr === null ? '—' : `${dp.growthRate3yr >= 0 ? '+' : ''}${(dp.growthRate3yr * 100).toFixed(1)}%`}
                          </td>
                          <td className="px-4 py-3 font-mono font-semibold text-ink">
                            {dp.intrinsicValuePerShare !== null ? `$${dp.intrinsicValuePerShare.toFixed(2)}` : '—'}
                          </td>
                          <td className="px-4 py-3 font-mono text-ink-600">
                            {dp.actualPrice !== null ? `$${dp.actualPrice.toFixed(2)}` : '—'}
                          </td>
                          <td className={cn('px-4 py-3 font-mono font-semibold',
                            dp.marginOfSafety === null ? 'text-ink-400' :
                            dp.marginOfSafety > 0.05 ? 'text-[#2F6B57]' :
                            dp.marginOfSafety < -0.05 ? 'text-[#B14228]' : 'text-ink-600'
                          )}>
                            {dp.marginOfSafety === null ? '—' : `${dp.marginOfSafety >= 0 ? '+' : ''}${(dp.marginOfSafety * 100).toFixed(1)}%`}
                          </td>
                          <td className={cn('px-4 py-3 font-mono',
                            dp.forwardReturn1yrPending ? 'text-ink-400' :
                            dp.forwardReturn1yr === null ? 'text-ink-400' :
                            dp.forwardReturn1yr >= 0 ? 'text-[#2F6B57] font-semibold' : 'text-[#B14228] font-semibold'
                          )}>
                            {dp.forwardReturn1yrPending
                              ? <Clock className="w-3.5 h-3.5 inline-block" />
                              : dp.forwardReturn1yr === null ? '—'
                              : `${dp.forwardReturn1yr >= 0 ? '+' : ''}${(dp.forwardReturn1yr * 100).toFixed(1)}%`
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Disclaimer */}
              <p className="text-[11px] text-ink-500 text-center pb-4">
                Deterministic DCF only — WACC 9.0%, Terminal Multiple 15×, Y1–5 growth from 3-yr FCF CAGR (capped ±20–30%), Y6–10 at 50% of Y1–5. No qualitative Oracle judgment applied. Green rows: IV &gt; price by &gt;5%. Red rows: price &gt; IV by &gt;5%.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Verdict Tracker Sub-Tab ────────────────────────────────────────── */}
      {activeSubTab === 'tracker' && (
        <div className="space-y-6">
          {loading && !data ? (
            <div className="flex flex-col items-center justify-center p-12 min-h-[400px]">
              <RefreshCw className="w-8 h-8 animate-spin text-ink-400 mb-4" />
              <p className="text-ink-600 font-medium">Computing verdict performance…</p>
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md flex items-start gap-3 w-full">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-sm">Action Required</h4>
                  <p className="text-sm mt-1">{error}</p>
                  <button 
                    onClick={fetchPerformance} 
                    className="mt-3 px-4 py-2 bg-white border border-red-200 text-red-700 rounded shadow-sm text-sm font-medium hover:bg-neutral-50 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          ) : data?.message === 'no_data' ? (
            <div className="p-6 h-full flex flex-col items-center justify-center">
              <div className="oi-card p-10 max-w-lg text-center bg-surface-1">
                <BarChart2 className="w-12 h-12 text-ink-300 mx-auto mb-4" />
                <h3 className="font-brand font-medium text-xl text-ink-900 mb-2">Verdict Performance Tracker</h3>
                <p className="text-ink-600 text-sm">
                  No verdicts recorded yet. Run your first analysis to start tracking Oracle performance.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-brand font-medium text-2xl text-ink-900">Verdict Performance</h2>
          <p className="text-ink-500 text-sm mt-1">
            Historical price returns against real-time Oracle signals.
          </p>
        </div>
        <button 
          onClick={fetchPerformance} 
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 border border-ink-200 rounded-md text-sm text-ink-600 hover:bg-surface-2 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Warning Callout */}
      {data?.message === 'insufficient_data' && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-md flex gap-2 items-center text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <p>
            Tracking {data.totalEntries} verdict(s). Results become meaningful at 5+ verdicts — keep analyzing to build your track record.
          </p>
        </div>
      )}

      {/* [TRACK-1] Ingestion summary + superseded/regression toggle */}
      {data?.ingestSummary && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-500 border border-ink-100 bg-surface-2 rounded-md px-3 py-2">
          <span>
            {data.ingestSummary.read} rows → {data.ingestSummary.canonical} canonical · {data.ingestSummary.superseded} superseded · {data.ingestSummary.regression} regression · {data.ingestSummary.degenerate} degenerate · {data.ingestSummary.mosReconciled} MoS-reconciled
          </span>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showSupersededRegression}
              onChange={e => setShowSupersededRegression(e.target.checked)}
              className="rounded border-ink-300"
            />
            Show superseded / regression rows
          </label>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {data?.bucketStats.map((stat, i) => {
          const avgRet = stat.avgReturn90d !== null ? `${stat.avgReturn90d >= 0 ? '+' : ''}${(stat.avgReturn90d * 100).toFixed(1)}%` : '—';
          const avgAlpha = stat.avgAlpha90d !== null ? `${stat.avgAlpha90d >= 0 ? '+' : ''}${(stat.avgAlpha90d * 100).toFixed(1)}%` : '—';
          const isAlphaPos = stat.avgAlpha90d !== null && stat.avgAlpha90d >= 0;
          const isAlphaNeg = stat.avgAlpha90d !== null && stat.avgAlpha90d < 0;

          return (
            <div key={i} className={cn("oi-card p-5 bg-[#F0EADF] border-2", getCardStyle(stat.bucket))}>
              <div className="flex flex-col h-full">
                <div className="mb-4">
                  <h3 className="font-semibold text-ink-900 uppercase tracking-wider text-xs mb-1">
                    {stat.label}
                  </h3>
                  <p className="text-[10px] text-ink-500 uppercase tracking-widest leading-tight">
                    {stat.verdictLabels}
                  </p>
                </div>
                
                <div className="flex-1 flex flex-col justify-center mb-4">
                  <div className={cn("text-4xl font-brand font-medium mb-2", pctColor(stat.avgReturn90d, false))}>
                    {avgRet}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <span className="text-ink-500">avg 90d vs SPY:</span>
                    <span className={cn(
                      "flex items-center", 
                      isAlphaPos ? "text-[#2F6B57]" : isAlphaNeg ? "text-[#B14228]" : "text-ink-400"
                    )}>
                      {stat.avgAlpha90d !== null && (
                        isAlphaPos ? <ArrowUpRight className="w-3.5 h-3.5 mr-0.5" /> : <ArrowDownRight className="w-3.5 h-3.5 mr-0.5" />
                      )}
                      {avgAlpha}
                    </span>
                  </div>
                </div>

                <div className="text-xs text-ink-500 mt-auto pt-4 border-t border-ink-400/20">
                  {stat.count} verdicts · {stat.samples90d} measured
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Sort / Filter Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 py-2 border-b border-ink-200 border-opacity-50">
        <div className="flex space-x-2">
          {(['all', 'buy', 'hold', 'pass'] as const).map(b => (
            <button
              key={b}
              onClick={() => setFilterBucket(b)}
              className={cn(
                "px-3 py-1 text-xs font-semibold rounded-full border transition-colors capitalize",
                filterBucket === b 
                  ? "bg-ink-900 border-ink-900 text-white" 
                  : "bg-surface-1 border-ink-200 text-ink-600 hover:bg-surface-2"
              )}
            >
              {b === 'pass' ? 'Pass/Avoid' : b === 'buy' ? 'Buy-Side' : b}
            </button>
          ))}
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-xs font-semibold text-ink-500 uppercase tracking-widest">Sort:</span>
          <select 
            className="text-sm bg-transparent border-none text-ink-900 font-medium focus:ring-0 cursor-pointer"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as any)}
          >
            <option value="alpha90d">90d Alpha</option>
            <option value="return90d">90d Return</option>
            <option value="date">Date</option>
            <option value="ticker">A-Z</option>
          </select>
          <button 
            onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
            className="p-1 hover:bg-surface-2 rounded text-ink-500 transition-colors"
            title="Toggle sort direction"
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
          <button
            onClick={exportVerdictTrackerCSV}
            disabled={!data?.entries?.length}
            className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest border border-ink-200 rounded-md text-ink-600 hover:bg-surface-2 transition-colors disabled:opacity-40"
          >
            <Download className="w-3 h-3" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto oi-card bg-surface-1">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr>
              <th className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 border-b border-ink-200 whitespace-nowrap cursor-pointer hover:text-ink-600" onClick={() => toggleSort('ticker')}>Ticker</th>
              <th className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 border-b border-ink-200 whitespace-nowrap cursor-pointer hover:text-ink-600" onClick={() => toggleSort('date')}>Date</th>
              <th className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 border-b border-ink-200 whitespace-nowrap">Verdict</th>
              <th className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 border-b border-ink-200 text-right">IV</th>
              <th className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 border-b border-ink-200 text-right">Price</th>
              <th className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 border-b border-ink-200 text-right">30d</th>
              <th className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 border-b border-ink-200 text-right whitespace-nowrap cursor-pointer hover:text-ink-600" onClick={() => toggleSort('return90d')}>90d Return</th>
              <th className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 border-b border-ink-200 text-right">1yr</th>
              <th className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 border-b border-ink-200 text-right whitespace-nowrap cursor-pointer hover:text-ink-600" onClick={() => toggleSort('alpha90d')}>Alpha 90d</th>
              <th className="px-4 py-3 text-[9px] font-bold uppercase tracking-widest text-ink-400 border-b border-ink-200 text-right whitespace-nowrap">vs MoS</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.map(entry => (
              <tr key={entry.id} className="text-sm border-b border-ink-100 hover:bg-surface-2/50 transition-colors">
                <td className="px-4 py-3 font-mono font-bold text-ink-900">{entry.ticker}</td>
                <td className="px-4 py-3 text-ink-600 whitespace-nowrap">{formatDate(entry.analyzedAt)}</td>
                <td className="px-4 py-3">
                  <span className={cn("px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm", getVerdictPillStyle(entry.verdictBucket))}>
                    {entry.verdict}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs text-ink-600">
                  {entry.intrinsicValue ? `$${entry.intrinsicValue.toFixed(2)}` : '—'}
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs text-ink-600">
                  {entry.priceAtAnalysis ? `$${entry.priceAtAnalysis.toFixed(2)}` : '—'}
                </td>
                
                {/* 30d Return */}
                <td className="px-4 py-3 text-right font-mono text-xs">
                  {entry.pending30d 
                    ? <Clock className="w-3 h-3 text-ink-400 inline-block" /> 
                    : <span className={pctColor(entry.return30d, false)}>{formatPct(entry.return30d, false)}</span>
                  }
                </td>
                
                {/* 90d Return (Primary) */}
                <td className="px-4 py-3 text-right font-mono text-xs font-bold">
                  {entry.pending90d 
                    ? <Clock className="w-3 h-3 text-ink-400 inline-block" /> 
                    : <span className={pctColor(entry.return90d, false)}>{formatPct(entry.return90d, false)}</span>
                  }
                </td>
                
                {/* 365d Return */}
                <td className="px-4 py-3 text-right font-mono text-xs">
                  {entry.pending365d 
                    ? <Clock className="w-3 h-3 text-ink-400 inline-block" /> 
                    : <span className={pctColor(entry.return365d, false)}>{formatPct(entry.return365d, false)}</span>
                  }
                </td>

                {/* Alpha 90d */}
                <td className="px-4 py-3 text-right font-mono text-xs font-bold">
                  {entry.pending90d 
                    ? <Clock className="w-3 h-3 text-ink-400 inline-block" /> 
                    : <span className={pctColor(entry.alpha90d, false)}>{formatPct(entry.alpha90d, false)}</span>
                  }
                </td>

                <td className="px-4 py-3 text-right font-mono text-xs text-ink-600">
                  {entry.marginOfSafety !== null && entry.marginOfSafety !== undefined ? `${entry.marginOfSafety >= 0 ? '+' : ''}${Number(entry.marginOfSafety).toFixed(1)}%` : '—'}
                </td>
              </tr>
            ))}
            {filteredEntries.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-ink-500 text-sm">
                  No {filterBucket !== 'all' ? filterBucket : ''} verdicts found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer Note */}
      <div className="text-[11px] text-ink-500 mt-4 text-center pb-8 border-t border-ink-200/50 pt-4">
        Returns are price returns only (not including dividends). SPY ETF used as market benchmark. <Clock className="w-3 h-3 inline-block -mt-0.5 text-ink-400" /> = period not yet elapsed.
      </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
